import Card from 'components/card';
import StockCard from 'components/card/StockCard';
import SearchIcon from 'components/icons/SearchIcon';
import ingredientsImg from 'assets/img/ingredients.png';
import mobilityImg from 'assets/img/mobility.png';
import { getStock } from 'interfaces/stock';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdPallet, MdGridView, MdDehaze } from 'react-icons/md';
import MovementTable from './components/MovementTable';
import CreateButton from 'components/buttons/CreateButton';
import { getStockMovement } from 'interfaces/stockMovement';
import { useDisclosure } from '@chakra-ui/hooks';
import StockEntryModal from './components/StockEntryModal';
import StockExitModal from './components/StockExitModal';
import OnBoardingCard from 'views/admin/main/account/coursePage/components/OnBoardingCard';
import { useShepherdTour } from 'react-shepherd';
import SearchSelector from 'components/fields/SearchSelector';
import { getTags } from 'interfaces/tagProduct';
import * as XLSX from 'xlsx';
import { getMyInfo } from 'interfaces/user';
import { getSuppliers } from 'interfaces/supplier';
import ProductLabels from './components/ProductLabels';

const steps: any = [
    {
        title: '3/5 Créer vos clients',
        text: 'Rendez-vous dans la section Client pour importer vos clients',
        attachTo: {
            element: '.step-customers',
            on: 'left',
        },
        buttons: [
            {
                classes: 'shepherd-button-primary',
                text: 'Importer mes clients',
                action: function (this: any): any {
                    localStorage.setItem('onboarding-step', '3');
                    window.location.href = '/customers';
                },
            },
        ],
        id: 'step-customers',
    },
];

const StockList = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [stock, setStock] = useState(null);
    const [stockMovement, setStockMovement] = useState([]);
    const [toggleState, setToggleState] = useState(1);
    const [onboarding, setOnboarding] = useState(false);
    const [category, setCategory] = useState('all');
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [view, setView] = useState<'grid' | 'list'>('list');
    const [tags, setTags] = useState<any[]>([]);
    const [selectedTag, setSelectedTag] = useState('all');
    const [alertFilter, setAlertFilter] = useState<'all' | 'alert' | 'ok' | 'no-threshold'>('all');
    const [userCategories, setUserCategories] = useState<any[]>([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [sortColumn, setSortColumn] = useState<'name' | 'quantity' | 'supplier' | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [supplierFilter, setSupplierFilter] = useState<string>('all');
    const [allSuppliers, setAllSuppliers] = useState<any[]>([]);
    const [showAlertFilter, setShowAlertFilter] = useState(false);
    const [showSupplierFilter, setShowSupplierFilter] = useState(false);
    const [showTypeFilter, setShowTypeFilter] = useState(false);
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
    const [showLabels, setShowLabels] = useState(false);
    const alertFilterRef = useRef<HTMLTableHeaderCellElement>(null);
    const supplierFilterRef = useRef<HTMLTableHeaderCellElement>(null);
    const typeFilterRef = useRef<HTMLTableHeaderCellElement>(null);
    const {
        isOpen: isOpenExit,
        onOpen: onOpenExit,
        onClose: onCloseExit,
    } = useDisclosure();
    const tourOptions = {
        defaultStepOptions: {
            cancelIcon: {
                enabled: true,
            },
        },
        useModalOverlay: true,
    };
    const tour = useShepherdTour({ tourOptions, steps });

    const toggleTab = (index: number) => {
        setToggleState(index);
    };

    const initStock = async (categoryFilter?: string) => {
        const res = await getStock(categoryFilter);
        if (res.error) {
            navigate('/auth/login');
            return;
        }
        setStock(res.data);
    };

    const initStockMovement = async () => {
        const res = await getStockMovement();
        if (res.error) {
            navigate('/auth/login');
            return;
        }
        setStockMovement(res.data.data);
    };

    const initTags = async () => {
           try {
             const res: any = await getTags();
             if (Array.isArray(res)) setTags(res);
             else if (Array.isArray(res?.data)) setTags(res.data);
             else setTags([]);
           } catch (e) {
             console.error('Erreur lors du chargement des tags:', e);
             setTags([]);
           }
         };

         const initUserCategories = async () => {
           try {
             const res = await getMyInfo();
             if (res && res.data && res.data.categories) {
               setUserCategories(res.data.categories);
             }
           } catch (e) {
             console.error('Erreur lors du chargement des catégories:', e);
             setUserCategories([]);
           }
         };

         const initSuppliers = async () => {
           try {
             const res: any = await getSuppliers();
             const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
             setAllSuppliers(list);
           } catch (e) {
             console.error('Erreur lors du chargement des fournisseurs:', e);
             setAllSuppliers([]);
           }
         };

         const resolveProductTag = (p: any) => {
               const raw = p?.productTag ?? p?.tagId ?? null;
               if (!raw) return null;
               if (typeof raw === 'object') return raw;
               return tags.find((t: any) => t._id === raw) || null;
         };

         const getProductSupplierName = (product: any): string => {
           if (!product) return '';
           
           // Check if supplier is already an object with a name
           if (product.supplier?.displayName) return product.supplier.displayName;
           if (product.supplier?.company) return product.supplier.company;
           if (product.supplier?.name) return product.supplier.name;
           if (product.supplierName) return product.supplierName;
           
           // If suppliers is an array
           if (Array.isArray(product.suppliers) && product.suppliers.length > 0) {
             const firstSupplier = product.suppliers[0];
             
             // If it's already an object with a name
             if (typeof firstSupplier === 'object') {
               if (firstSupplier?.displayName) return firstSupplier.displayName;
               if (firstSupplier?.company) return firstSupplier.company;
               if (firstSupplier?.name) return firstSupplier.name;
             }
             
             // If it's an ID, look it up in allSuppliers
             if (typeof firstSupplier === 'string' && allSuppliers.length > 0) {
               const supplier = allSuppliers.find((s: any) => s._id === firstSupplier);
               if (supplier) {
                 return supplier.displayName || supplier.company || supplier.name || '';
               }
             }
           }
           
           return '';
         };

    const handleSort = (column: 'name' | 'quantity' | 'supplier') => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const sortProducts = (products: any[]) => {
        if (!sortColumn) return products;
        
        return [...products].sort((a, b) => {
            let aValue, bValue;
            
            if (sortColumn === 'name') {
                aValue = (a.name || '').toLowerCase();
                bValue = (b.name || '').toLowerCase();
                return sortDirection === 'asc' 
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            } else if (sortColumn === 'quantity') {
                aValue = Number(a.quantity ?? 0);
                bValue = Number(b.quantity ?? 0);
                return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            } else if (sortColumn === 'supplier') {
                aValue = getProductSupplierName(a).toLowerCase();
                bValue = getProductSupplierName(b).toLowerCase();
                return sortDirection === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }
            return 0;
        });
    };

    const getUniqueSuppliers = () => {
        if (!stock) return [];
        const suppliers = new Set<string>();
        stock.forEach((item: any) => {
            const supplierName = getProductSupplierName(item);
            if (supplierName && supplierName.trim() !== '') {
                suppliers.add(supplierName.trim());
            }
        });
        return Array.from(suppliers).sort();
    };

    useEffect(() => {
        const onboardingStep = localStorage.getItem('onboarding-step');
        const queryParameters = new URLSearchParams(window.location.search);
        const page = queryParameters.get('page');
        const searchParam = queryParameters.get('search');
        const tagParam = queryParameters.get('tag');
        const viewParam = queryParameters.get('view');
        const categoryParam = queryParameters.get('category');
        const alertFilterParam = queryParameters.get('alertFilter');
        const supplierFilterParam = queryParameters.get('supplierFilter');
        const selectedCategoryParam = queryParameters.get('selectedCategory');
        const sortColumnParam = queryParameters.get('sortColumn');
        const sortDirectionParam = queryParameters.get('sortDirection');
        const toggleStateParam = queryParameters.get('toggleState');
        const typeFilterParam = queryParameters.get('typeFilter');
        
        if (searchParam) setSearch(searchParam);
        if (tagParam) setSelectedTag(tagParam);
        if (categoryParam) setCategory(categoryParam);
        if (alertFilterParam && ['all', 'alert', 'ok', 'no-threshold'].includes(alertFilterParam)) {
            setAlertFilter(alertFilterParam as 'all' | 'alert' | 'ok' | 'no-threshold');
        }
        if (supplierFilterParam) setSupplierFilter(supplierFilterParam);
        if (selectedCategoryParam) setSelectedCategory(selectedCategoryParam);
        if (sortColumnParam && ['name', 'quantity', 'supplier'].includes(sortColumnParam)) {
            setSortColumn(sortColumnParam as 'name' | 'quantity' | 'supplier');
        }
        if (sortDirectionParam && ['asc', 'desc'].includes(sortDirectionParam)) {
            setSortDirection(sortDirectionParam as 'asc' | 'desc');
        }
        if (toggleStateParam) {
            const state = parseInt(toggleStateParam);
            if (state >= 1 && state <= 5) setToggleState(state);
        }
        if (typeFilterParam) setTypeFilter(typeFilterParam);
        if (viewParam === 'list' || viewParam === 'grid'){
            setView(viewParam as 'list' | 'grid');
        } else{
            const savedView = localStorage.getItem('stock-view');
            if (savedView === 'list' || savedView === 'grid') setView(savedView as 'list' | 'grid');
        }
        if (page && page === 'stock') {
            setToggleState(5);
            onOpen();
        }
        initStock();
        initStockMovement();
        initTags();
        initUserCategories();
        initSuppliers();
        if (onboardingStep === '2') setOnboarding(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
           const sp = new URLSearchParams(window.location.search);
           if (search && search.trim() !== '') sp.set('search', search);
           else sp.delete('search');
           if (selectedTag && selectedTag !== 'all') sp.set('tag', selectedTag);
           else sp.delete('tag');
           if (category && category !== 'all') sp.set('category', category);
           else sp.delete('category');
           if (alertFilter && alertFilter !== 'all') sp.set('alertFilter', alertFilter);
           else sp.delete('alertFilter');
           if (supplierFilter && supplierFilter !== 'all') sp.set('supplierFilter', supplierFilter);
           else sp.delete('supplierFilter');
           if (selectedCategory && selectedCategory !== 'all') sp.set('selectedCategory', selectedCategory);
           else sp.delete('selectedCategory');
           if (sortColumn) sp.set('sortColumn', sortColumn);
           else sp.delete('sortColumn');
           if (sortDirection) sp.set('sortDirection', sortDirection);
           else sp.delete('sortDirection');
           if (toggleState && toggleState !== 1) sp.set('toggleState', toggleState.toString());
           else sp.delete('toggleState');
           if (typeFilter && typeFilter !== 'all') sp.set('typeFilter', typeFilter);
           else sp.delete('typeFilter');
           sp.set('view', view);
           const newUrl = `${window.location.pathname}${sp.toString() ? `?${sp.toString()}` : ''}`;
           window.history.replaceState(null, '', newUrl);
         }, [search, selectedTag, view, category, alertFilter, supplierFilter, selectedCategory, sortColumn, sortDirection, toggleState, typeFilter]);

    useEffect(() => {
        localStorage.setItem('stock_view', view);
    }, [view]);

    useEffect(() => {
        initStock(selectedCategory);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategory]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (alertFilterRef.current && !alertFilterRef.current.contains(event.target as Node)) {
                setShowAlertFilter(false);
            }
            if (supplierFilterRef.current && !supplierFilterRef.current.contains(event.target as Node)) {
                setShowSupplierFilter(false);
            }
            if (typeFilterRef.current && !typeFilterRef.current.contains(event.target as Node)) {
                setShowTypeFilter(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const SkelettonProduct = () => {
        return (
            <div
                className={`animate-pulse relative flex items-center rounded-[20px] bg-clip-border border px-[18px] py-4 shadow-3xl shadow-shadow-500 hover:cursor-pointer dark:!bg-navy-800 dark:shadow-none`}
            >
                <div className="grid grid-cols-12">
                    <div className="-bottom-12 col-span-4 flex h-[87px] w-[87px] items-center justify-center rounded-full">
                        <div className="h-[80px] w-[80px] rounded-xl bg-gray-200"></div>
                    </div>
                    <div className="col-span-8 ml-4">
                        <div className="mt-2 w-[99%] h-4 bg-navy-700/50 rounded-full"></div>
                        <div className="my-1 w-[60px] h-2 bg-gray-600 rounded-full"></div>
                        <p className=" mb-1 text-sm font-medium text-gray-600">
                            <div className="w-5 h-3 bg-gray-600 rounded-full"></div>
                        </p>
                        <div className="w-[60px] h-5 bg-gray-600  rounded-full">
                            {' '}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const StockMovement = () => {
        return (
            <div className="col-span-12">
                {stockMovement && stockMovement.length > 0 && (
                    <MovementTable tableData={stockMovement} />
                )}
                {stockMovement && stockMovement.length === 0 && (
                    <div className="col-span-12 mt-10">
                        <img
                            className="mx-auto flex h-[120px] w-[120px] items-center justify-center"
                            src={mobilityImg}
                            alt=""
                        />
                        <div className="font-large mt-5 flex items-center justify-center gap-5 rounded-xl">
                            <button
                                className="col-span-12 rounded-xl border-2 border-[#0260CB] px-5 py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 dark:border-brand-400 dark:bg-brand-400/10 dark:text-white dark:hover:bg-brand-300/10 dark:active:bg-brand-200/10 md:col-span-2 xl:col-start-9"
                                onClick={onOpen}
                            >
                                Entrer du stock
                            </button>
                            <button
                                className="col-span-12 rounded-xl border-2 border-[#0260CB] px-5 py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 dark:border-brand-400 dark:bg-brand-400/10 dark:text-white dark:hover:bg-brand-300/10 dark:active:bg-brand-200/10 md:col-span-2 xl:col-start-11"
                                onClick={onOpenExit}
                            >
                                Sortir du stock
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const searchProduct = (product: any, search: string) => {
        if (!search) return true;

        const escapeRegex = (text: string) =>
            text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        const fuzzify = (term: string): RegExp => {
            const escaped = escapeRegex(term);
            const length = term.length;

            if (length <= 1) {
                return new RegExp(`\\b${escaped}\\b`, 'i');
            }

            if (length <= 4) {
                const loose = escaped.split('').join('.{0,2}');
                return new RegExp(loose, 'i');
            }

            const fuzzy = escaped.split('').join('.*');
            return new RegExp(fuzzy, 'i');
        };

        const terms = search.trim().toLowerCase().split(/\s+/);

        return terms.every((term) => {
            const regex = fuzzify(term);
            for (const key in product) {
                const value = product[key];
                if (
                    typeof value === 'string' &&
                    regex.test(value.toLowerCase())
                ) {
                    return true;
                }
            }
            return false;
        });
    };

    const filterStockPerZone: any = (stock: any, areaIndex: number) => {
        const areas = ['', '', 'zone-a', 'zone-b', 'zone-c', ''];
        if (areas[areaIndex] === '') return stock;
        let newStock = JSON.parse(JSON.stringify(stock));
        newStock = newStock.map((item: any) => {
            let quantity = 0;
            item.inventory.forEach((inventory: any) => {
                if (inventory.area === areas[areaIndex]) {
                    quantity += inventory.quantity;
                }
            });
            item.quantity = quantity;
            return item;
        });
        if (areaIndex > 1)
            return newStock.filter((item: any) => item.quantity);
        return newStock;
    };

    const refreshData = async () => {
        initStockMovement();
        initStock();
    };

    const toggleSelectProduct = (productId: string) => {
        const newSelected = new Set(selectedProducts);
        if (newSelected.has(productId)) {
            newSelected.delete(productId);
        } else {
            newSelected.add(productId);
        }
        setSelectedProducts(newSelected);
    };

    const toggleSelectAll = () => {
        if (!stock) return;
        const filteredStock = filterStockPerZone(stock, toggleState)
            .filter((e: any) => searchProduct(e, search))
            .filter((e: any) => (category === 'all' ? true : e.productType === category))
            .filter((e: any) => selectedTag === 'all' ? true : (resolveProductTag(e)?._id === selectedTag))
            .filter((e: any) => {
                if (typeFilter === 'all') return true;
                return e.productType === typeFilter;
            })
            .filter((e: any) => {
                if (alertFilter === 'all') return true;
                const quantity = Number(e.quantity ?? 0);
                const threshold = Number(e.alert);
                const hasThreshold = !Number.isNaN(threshold);
                if (!hasThreshold) return alertFilter === 'no-threshold';
                if (alertFilter === 'alert') return quantity <= threshold;
                if (alertFilter === 'ok') return quantity > threshold;
                return true;
            })
            .filter((e: any) => {
                if (supplierFilter === 'all') return true;
                const supplierName = getProductSupplierName(e);
                if (supplierFilter === 'none') return !supplierName || supplierName.trim() === '';
                return supplierName.trim() === supplierFilter;
            });

        if (selectedProducts.size === filteredStock.length) {
            setSelectedProducts(new Set());
        } else {
            setSelectedProducts(new Set(filteredStock.map((p: any) => p._id)));
        }
    };

    const generateLabels = () => {
        if (selectedProducts.size === 0) {
            alert('Veuillez sélectionner au moins un produit');
            return;
        }
        setShowLabels(true);
    };

    const getSelectedProductsData = () => {
        if (!stock) return [];
        const filtered = stock.filter((p: any) => selectedProducts.has(p._id));
        return filtered;
    };

    const exportToExcel = () => {
        if (!stock || stock.length === 0) return;

        const filteredStock = filterStockPerZone(stock, toggleState)
            .filter((e: any) => searchProduct(e, search))
            .filter((e: any) => (category === 'all' ? true : e.productType === category))
            .filter((e: any) => selectedTag === 'all' ? true : (resolveProductTag(e)?._id === selectedTag))
            .filter((e: any) => {
                if (typeFilter === 'all') return true;
                return e.productType === typeFilter;
            })
            .filter((e: any) => {
                if (alertFilter === 'all') return true;
                const quantity = Number(e.quantity ?? 0);
                const threshold = Number(e.alert);
                const hasThreshold = !Number.isNaN(threshold);
                if (!hasThreshold) return alertFilter === 'no-threshold';
                if (alertFilter === 'alert') return quantity <= threshold;
                if (alertFilter === 'ok') return quantity > threshold;
                return true;
            });

        const excelData = filteredStock.map((item: any) => ({
            'Référence': item.ref || '',
            'Nom': item.name || '',
            'Quantité': item.quantity ?? 0,
            'Unité': item.unity || '',
            'Inventaire': item.inventory?.map((inv: any) => `${inv.area}: ${inv.quantity}`).join(' | ') || ''
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Inventaire');
        
        const date = new Date().toISOString().split('T')[0];
        XLSX.writeFile(workbook, `inventaire_stock_${date}.xlsx`);
    };

    return (
        <>
            {showLabels && (
                <ProductLabels
                    products={getSelectedProductsData()}
                    onClose={() => setShowLabels(false)}
                />
            )}
            <StockEntryModal
                onClose={onClose}
                isOpen={isOpen}
                stock={stock}
                onDone={refreshData}
            />
            <StockExitModal
                onClose={onCloseExit}
                isOpen={isOpenExit}
                stock={stock}
                onDone={refreshData}
            />
            {onboarding && (
                <div className="mt-5">
                    <OnBoardingCard
                        title="Créer vos produits"
                        actualStep={2}
                        totalStep={5}
                        description="Vous pouvez créer tout vos produits ici avant de passer à l'étape suivante"
                        onClick={() => tour.start()}
                    />
                </div>
            )}
            <Card extra={'w-full h-full sm:overflow-auto p-5 border'}>
                
                {/* Tabs */}
                <div className="h-full w-full flex-col px-4 lg:px-3">
                
                    <div className="mt-2 flex w-full justify-between gap-0 sm:gap-0 md:gap-2 xl:gap-14 overflow-hidden border-b border-gray-200 text-3xl lg:gap-[50px] 2xl:justify-center 2xl:gap-[70px]">
                    {toggleState < 5 && (
                        <div className="hidden flex lg:block">
                            <div className="flex gap-2 ml-auto">
                                 <MdGridView
                                    size={20}
                                    className={`${view === 'grid' && 'text-toola-500'} cursor-pointer`}
                                    title="Vue cartes"
                                    onClick={() => setView('grid')}
                                />
                                <MdDehaze
                                    size={20}
                                    className={`${view === 'list' && 'text-toola-500'} cursor-pointer`}
                                    title="Vue tableau"
                                    onClick={() => setView('list')}
                                />
                            </div>
                        </div>
                    )}
                        <div
                            className={
                                toggleState === 1
                                    ? ' flex items-center gap-2 border-b-[4px] border-[#0260CB] pb-3 hover:cursor-pointer dark:border-brand-400'
                                    : 'flex items-center gap-2 pb-[20px] hover:cursor-pointer'
                            }
                            onClick={() => toggleTab(1)}
                        >
                            <p
                                className={`text-sm md:text-lg text-[#0260CB] dark:text-white`}
                            >
                                <MdPallet />
                            </p>
                            <p className="text-xs md:text-[18px] font-medium text-navy-700 dark:text-white">
                                Toutes les zones
                            </p>
                        </div>

                        <div
                            className={
                                toggleState === 2
                                    ? ' flex items-center gap-2 border-b-[4px] border-[#0260CB] pb-3 hover:cursor-pointer dark:border-brand-400'
                                    : 'flex items-center gap-2 pb-[20px] hover:cursor-pointer'
                            }
                            onClick={() => toggleTab(2)}
                        >
                            <p className="text-sm md:text-lg text-navy-700 dark:text-white">
                                <MdPallet />
                            </p>
                            <p className="text-xs md:text-[18px] font-medium text-navy-700 dark:text-white">
                                Zone A
                            </p>
                        </div>

                        <div
                            className={
                                toggleState === 3
                                    ? ' flex items-center gap-2 border-b-[4px] border-[#0260CB] pb-3 hover:cursor-pointer dark:border-brand-400'
                                    : 'flex items-center gap-2 pb-[20px] hover:cursor-pointer'
                            }
                            onClick={() => toggleTab(3)}
                        >
                            <p className="text-sm md:text-lg text-navy-700 dark:text-white">
                                <MdPallet />
                            </p>
                            <p className="text-xs md:text-[18px] font-medium text-navy-700 dark:text-white">
                                Zone B
                            </p>
                        </div>

                        <div
                            className={
                                toggleState === 4
                                    ? ' flex items-center gap-2 border-b-[4px] border-[#0260CB] pb-3 hover:cursor-pointer dark:border-brand-400'
                                    : 'flex items-center gap-2 pb-[20px] hover:cursor-pointer'
                            }
                            onClick={() => toggleTab(4)}
                        >
                            <p className="text-sm md:text-lg text-navy-700 dark:text-white">
                                <MdPallet />
                            </p>
                            <p className="text-xs md:text-[18px] font-medium text-navy-700 dark:text-white">
                                Zone C
                            </p>
                        </div>

                        {/* <div
                                className={
                                    toggleState === 5
                                        ? " flex items-center gap-2 border-b-[4px] border-[#0260CB] pb-3 hover:cursor-pointer dark:border-brand-400"
                                        : "flex items-center gap-2 pb-[20px] hover:cursor-pointer"
                                }
                                onClick={() => toggleTab(5)}
                            >
                                <p className="text-lg text-navy-700 dark:text-white">
                                    <MdOutlineMoveDown />
                                </p>
                                <p className="text-sm md:text-lg md:text-[18px] font-medium text-navy-700 dark:text-white">
                                    Mouvements
                                </p>
                            </div> */}
                    </div>
                </div>
                {/* Search bar */}
                <div className="grid max-w-full grid-cols-12 items-center gap-3 rounded-xl pt-5">
                    <div className="col-span-12 flex h-[38px] flex-grow items-center rounded-xl bg-lightPrimary text-sm text-gray-600 dark:!bg-navy-900 dark:text-white lg:col-span-4">
                        <SearchIcon />
                        <input
                            type="text"
                            placeholder="Recherche...."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="block w-full rounded-full bg-lightPrimary text-base text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                        />
                    </div>
                    {/* onClick={() => navigate("/create-product")} */}
                    {stock && stock.length > 0 && toggleState !== 5 && (
                        <>
                            <CreateButton
                                value="Créer un produit"
                                extra=" text-base col-start-1 col-span-6 md:col-span-4 lg:col-start-7 lg:col-span-2 3xl:col-start-8 3xl:col-span-2"
                                onClick={() => navigate('/create-product')}
                            />
                            <button
                                className="col-span-6 rounded-xl border-2 border-[#0260CB] py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 md:col-span-3 lg:col-span-2"
                                onClick={() => navigate('/stock/movements')}
                            >
                                Gérer le stock
                            </button>
                            <button
                                className="col-span-6 rounded-xl border-2 border-green-600 py-3 text-base font-medium text-green-600 transition duration-200 hover:bg-green-600/5 active:bg-green-700/5 md:col-span-3 lg:col-span-2"
                                onClick={exportToExcel}
                            >
                                Télécharger Excel
                            </button>
                            <div className="col-span-12 mt-2 flex justify-end">
                                <button
                                    className="text-sm text-gray-500 underline hover:text-gray-700 transition-colors"
                                    onClick={() => navigate('/archived-products')}
                                >
                                    Produits archivés
                                </button>
                            </div>
                        </>
                    )}
                    {toggleState < 5 && stock && stock.length > 0 && selectedProducts.size > 0 && (
                        <button
                            className="col-span-6 rounded-xl border-2 border-[#0260CB] py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-[#0260CB]/5 active:bg-[#0260CB]/10 md:col-span-3 lg:col-span-2"
                            onClick={generateLabels}
                        >
                            Étiquettes ({selectedProducts.size})
                        </button>
                    )}
                    {toggleState === 5 &&
                        stock &&
                        stock.length > 0 &&
                        stockMovement.length > 0 && (
                            <>
                                <button
                                    className="col-span-12 rounded-xl border-2 border-[#0260CB] px-5 py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 dark:border-brand-400 dark:bg-brand-400/10 dark:text-white dark:hover:bg-brand-300/10 dark:active:bg-brand-200/10 md:col-span-2 xl:col-start-9"
                                    onClick={onOpen}
                                >
                                    Entrer du stock
                                </button>
                                <button
                                    className="col-span-12 rounded-xl border-2 border-[#0260CB] px-5 py-3 text-base font-medium text-[#0260CB] transition duration-200 hover:bg-brand-600/5 active:bg-brand-700/5 dark:border-brand-400 dark:bg-brand-400/10 dark:text-white dark:hover:bg-brand-300/10 dark:active:bg-brand-200/10 md:col-span-2 xl:col-start-11"
                                    onClick={onOpenExit}
                                >
                                    Sortir du stock
                                </button>
                            </>
                        )}
                </div>
                <div className="flex justify-between flex-wrap gap-2">
                    <div className="mt-4 flex gap-2 overflow-x-auto h-7">
                        <p
                            className={`bg-white rounded-xl py-1 px-3 text-sm cursor-pointer border ${
                                'all' === category && 'font-bold'
                            }`}
                            onClick={() => setCategory('all')}
                        >
                            Tout
                        </p>
                        <p
                            className={`bg-white rounded-xl py-1 px-3 text-sm cursor-pointer border ${
                                'brut' === category && 'font-bold'
                            }`}
                            onClick={() => setCategory('brut')}
                        >
                            Produit brut
                        </p>
                        <p
                            className={`bg-white rounded-xl py-1 px-3 text-sm cursor-pointer border ${
                                'transformed' === category && 'font-bold'
                            }`}
                            onClick={() => setCategory('transformed')}
                        >
                            Produit transformé
                        </p>
                        <p
                            className={`bg-white rounded-xl py-1 px-3 text-sm cursor-pointer border ${
                                'assembled' === category && 'font-bold'
                            }`}
                            onClick={() => setCategory('assembled')}
                        >
                            Produit assemblé
                        </p>
                    </div>
                    <div className="flex gap-2 items-center">
                        <div className="pt-3 z-50 w-[180px] text-sm">
                            <SearchSelector
                                displayKey="name"
                                options={[{ name: 'Toutes catégories', id: 'all', _id: 'all' }, { name: 'Produit vendable', id: 'all', _id: 'all' }, { name: 'Produit non vendable', id: 'hidden', _id: 'hidden' }, ...userCategories]}
                                value={selectedCategory}
                                onSelect={(c: any) => setSelectedCategory(c?.id ?? c?._id ?? 'all')}
                                defaultKey="id"
                                canCreate={false}
                                inputSmall={true}
                            />
                        </div>
                        <div className="pt-3 z-50 w-[180px] text-sm">
                            <SearchSelector
                                displayKey="name"
                                options={[{ name: 'Tous les tags', _id: 'all' }, ...tags]}
                                value={selectedTag}
                                onSelect={(t: any) => setSelectedTag(t?._id ?? 'all')}
                                defaultKey="_id"
                                canCreate={false}
                                inputSmall={true}
                            />
                        </div>
                    </div>
                </div>
                {/* Skelleton Loader */}
                {!stock && (
                    <div className="z-0 col-span-12 mb-10 mt-2 grid h-full w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                        {[
                            1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15,
                            16,
                        ].map((item, index) => (
                            <SkelettonProduct key={index} />
                        ))}
                    </div>
                )}
                {/* Items list */}
                <div className="z-0 col-span-12 mb-10 mt-2 grid h-full w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                                        
                    {/* Items list */}
                    {stock && toggleState < 5 && (() => {
                      const filteredStock = sortProducts(
                        filterStockPerZone(stock, toggleState)
                          .filter((e: any) => searchProduct(e, search))
                          .filter((e: any) => (category === 'all' ? true : e.productType === category))
                          .filter((e: any) => selectedTag === 'all' ? true : (resolveProductTag(e)?._id === selectedTag))
                          .filter((e: any) => {
                              if (typeFilter === 'all') return true;
                              return e.productType === typeFilter;
                          })
                          .filter((e: any) => {
                              if (alertFilter === 'all') return true;
                              const quantity = Number(e.quantity ?? 0);
                              const threshold = Number(e.alert);
                              const hasThreshold = !Number.isNaN(threshold);
                              if (!hasThreshold) return alertFilter === 'no-threshold';
                              if (alertFilter === 'alert') return quantity <= threshold;
                              if (alertFilter === 'ok') return quantity > threshold;
                              return true;
                          })
                          .filter((e: any) => {
                              if (supplierFilter === 'all') return true;
                              const supplierName = getProductSupplierName(e);
                              if (supplierFilter === 'none') return !supplierName || supplierName.trim() === '';
                              return supplierName.trim() === supplierFilter;
                          })
                      ).reverse();
                    
                      return (
                        <>
                          {/* Vue tableau */}
                          {view === 'list' && (
                            <div className="z-0 col-span-12 mb-10 mt-4 rounded-lg border">
                              <div className="relative overflow-x-auto">
                                <table className="w-full text-sm text-left text-gray-500">
                                  <thead className="text-gray-700 bg-gray-50">
                                    <tr>
                                      <th scope="col" className="px-2 py-3 w-10">
                                        <input
                                          type="checkbox"
                                          checked={selectedProducts.size > 0 && selectedProducts.size === filteredStock.length}
                                          onChange={toggleSelectAll}
                                          className="w-4 h-4 cursor-pointer"
                                        />
                                      </th>
                                      <th 
                                        scope="col" 
                                        className="px-2 py-3 cursor-pointer hover:bg-gray-100 select-none"
                                        onClick={() => handleSort('name')}
                                      >
                                        <div className="flex items-center gap-1">
                                          Produit
                                          {sortColumn === 'name' && (
                                            <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                          )}
                                        </div>
                                      </th>
                                      <th scope="col" className="px-2 py-3">Référence</th>
                                      <th scope="col" className="px-2 py-3 relative" ref={typeFilterRef}>
                                        <div className="flex items-center gap-2">
                                          <span
                                            className="cursor-pointer hover:text-gray-900 select-none"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setShowTypeFilter(!showTypeFilter);
                                            }}
                                          >
                                            Type
                                            <span className="ml-1 text-[#0260CB]">{typeFilter !== 'all' ? '●' : '▼'}</span>
                                          </span>
                                        </div>
                                        {showTypeFilter && (
                                          <div 
                                            className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-300 rounded-md shadow-lg p-2 min-w-[180px]"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <div className="flex flex-col gap-1">
                                              <button
                                                onClick={() => {
                                                  setTypeFilter('all');
                                                  setShowTypeFilter(false);
                                                }}
                                                className={`text-left px-2 py-1 rounded text-sm hover:bg-gray-100 ${typeFilter === 'all' ? 'bg-[#0260CB]/10 font-semibold text-[#0260CB]' : ''}`}
                                              >
                                                Tous
                                              </button>
                                              <button
                                                onClick={() => {
                                                  setTypeFilter('brut');
                                                  setShowTypeFilter(false);
                                                }}
                                                className={`text-left px-2 py-1 rounded text-sm hover:bg-gray-100 ${typeFilter === 'brut' ? 'bg-[#0260CB]/10 font-semibold text-[#0260CB]' : ''}`}
                                              >
                                                Produit brut
                                              </button>
                                              <button
                                                onClick={() => {
                                                  setTypeFilter('transformed');
                                                  setShowTypeFilter(false);
                                                }}
                                                className={`text-left px-2 py-1 rounded text-sm hover:bg-gray-100 ${typeFilter === 'transformed' ? 'bg-[#0260CB]/10 font-semibold text-[#0260CB]' : ''}`}
                                              >
                                                Produit transformé
                                              </button>
                                              <button
                                                onClick={() => {
                                                  setTypeFilter('assembled');
                                                  setShowTypeFilter(false);
                                                }}
                                                className={`text-left px-2 py-1 rounded text-sm hover:bg-gray-100 ${typeFilter === 'assembled' ? 'bg-[#0260CB]/10 font-semibold text-[#0260CB]' : ''}`}
                                              >
                                                Produit assemblé
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </th>
                                      <th scope="col" className="px-2 py-3">Unité</th>
                                      <th 
                                        scope="col" 
                                        className="px-2 py-3 cursor-pointer hover:bg-gray-100 select-none"
                                        onClick={() => handleSort('quantity')}
                                      >
                                        <div className="flex items-center gap-1">
                                          Stock
                                          {sortColumn === 'quantity' && (
                                            <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                          )}
                                        </div>
                                      </th>
                                      <th scope="col" className="px-2 py-3 relative" ref={alertFilterRef}>
                                        <div className="flex items-center gap-2">
                                          <span
                                            className="cursor-pointer hover:text-gray-900 select-none"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setShowAlertFilter(!showAlertFilter);
                                            }}
                                          >
                                            Alerte
                                            <span className="ml-1 text-[#0260CB]">{alertFilter !== 'all' ? '●' : '▼'}</span>
                                          </span>
                                        </div>
                                        {showAlertFilter && (
                                          <div 
                                            className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-300 rounded-md shadow-lg p-2 min-w-[150px]"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <div className="flex flex-col gap-1">
                                              <button
                                                onClick={() => {
                                                  setAlertFilter('all');
                                                  setShowAlertFilter(false);
                                                }}
                                                className={`text-left px-2 py-1 rounded text-sm hover:bg-gray-100 ${alertFilter === 'all' ? 'bg-[#0260CB]/10 font-semibold text-[#0260CB]' : ''}`}
                                              >
                                                Toutes
                                              </button>
                                              <button
                                                onClick={() => {
                                                  setAlertFilter('alert');
                                                  setShowAlertFilter(false);
                                                }}
                                                className={`text-left px-2 py-1 rounded text-sm hover:bg-gray-100 ${alertFilter === 'alert' ? 'bg-[#0260CB]/10 font-semibold text-[#0260CB]' : ''}`}
                                              >
                                                En alerte
                                              </button>
                                              <button
                                                onClick={() => {
                                                  setAlertFilter('ok');
                                                  setShowAlertFilter(false);
                                                }}
                                                className={`text-left px-2 py-1 rounded text-sm hover:bg-gray-100 ${alertFilter === 'ok' ? 'bg-[#0260CB]/10 font-semibold text-[#0260CB]' : ''}`}
                                              >
                                                En stock
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      </th>
                                      <th scope="col" className="px-2 py-3 relative" ref={supplierFilterRef}>
                                        <div className="flex items-center gap-2">
                                          <span
                                            className="cursor-pointer hover:text-gray-900 select-none"
                                            onClick={() => handleSort('supplier')}
                                          >
                                            Fournisseur
                                            {sortColumn === 'supplier' && (
                                              <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                            )}
                                          </span>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setShowSupplierFilter(!showSupplierFilter);
                                            }}
                                            className="text-sm hover:text-gray-900"
                                          >
                                            <span className="text-[#0260CB]">{supplierFilter !== 'all' ? '●' : '▼'}</span>
                                          </button>
                                        </div>
                                        {showSupplierFilter && (
                                          <div 
                                            className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-300 rounded-md shadow-lg p-2 min-w-[180px] max-h-[250px] overflow-y-auto"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <div className="flex flex-col gap-1">
                                              <button
                                                onClick={() => {
                                                  setSupplierFilter('all');
                                                  setShowSupplierFilter(false);
                                                }}
                                                className={`text-left px-2 py-1 rounded text-sm hover:bg-gray-100 ${supplierFilter === 'all' ? 'bg-[#0260CB]/10 font-semibold text-[#0260CB]' : ''}`}
                                              >
                                                Tous
                                              </button>
                                              <button
                                                onClick={() => {
                                                  setSupplierFilter('none');
                                                  setShowSupplierFilter(false);
                                                }}
                                                className={`text-left px-2 py-1 rounded text-sm hover:bg-gray-100 ${supplierFilter === 'none' ? 'bg-[#0260CB]/10 font-semibold text-[#0260CB]' : ''}`}
                                              >
                                                Sans fournisseur
                                              </button>
                                              {getUniqueSuppliers().map((supplier) => (
                                                <button
                                                  key={supplier}
                                                  onClick={() => {
                                                    setSupplierFilter(supplier);
                                                    setShowSupplierFilter(false);
                                                  }}
                                                  className={`text-left px-2 py-1 rounded text-sm hover:bg-gray-100 ${supplierFilter === supplier ? 'bg-[#0260CB]/10 font-semibold text-[#0260CB]' : ''}`}
                                                >
                                                  {supplier}
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </th>
                                      <th scope="col" className="px-2 py-3">Prix</th>
                                      <th scope="col" className="px-2 py-3">Tags</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filteredStock.map((item: any, index: number) => (
                                      <tr
                                        key={item._id || index}
                                        className="bg-white border-b text-gray-800 border-gray-200 hover:bg-gray-100"
                                      >
                                        <td className="px-2 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                                          <input
                                            type="checkbox"
                                            checked={selectedProducts.has(item._id)}
                                            onChange={() => toggleSelectProduct(item._id)}
                                            className="w-4 h-4 cursor-pointer"
                                          />
                                        </td>
                                        <td 
                                          className="px-2 py-3 max-w-[260px] cursor-pointer"
                                          onClick={() => navigate(`/update-product/${item._id}`)}
                                        >
                                          <p className="font-medium text-gray-900 truncate">{item.name}</p>
                                        </td>
                                        <td className="px-2 py-3 cursor-pointer" onClick={() => navigate(`/update-product/${item._id}`)}>{item.ref || '-'}</td>
                                        <td className="px-2 py-3 cursor-pointer" onClick={() => navigate(`/update-product/${item._id}`)}>
                                          {item.productType === 'brut'
                                            ? 'Produit brut'
                                            : item.productType === 'transformed'
                                            ? 'Produit transformé'
                                            : item.productType === 'assembled'
                                            ? 'Produit assemblé'
                                            : '-'}
                                        </td>
                                        <td className="px-2 py-3 cursor-pointer" onClick={() => navigate(`/update-product/${item._id}`)}>{item.unity || '-'}</td>
                                        <td className="px-2 py-3 cursor-pointer" onClick={() => navigate(`/update-product/${item._id}`)}>{Number(item.quantity ?? 0).toFixed(2).replace(/\.?0+$/, '')}</td>
                                        <td className="px-2 py-3 cursor-pointer" onClick={() => navigate(`/update-product/${item._id}`)}>
                                            <span className="text-gray-700">Seuil: </span>
                                        {(() => {
                                            const quantity = Number(item.quantity ?? 0);
                                            const alert = Number(item.alert ?? NaN);
                                            return (
                                        <>
                                            {quantity <= alert && (
                                        <>
                                            <span className="text-red-500">alerte </span>
                                            <span className="text-red-500">!</span>
                                        </>
                                            )}
                                            {quantity > alert && <span className="text-green-500">OK</span>}
                                        </>
                                            );
                                        })()}
                                        </td>
                                        <td className="px-2 py-3 cursor-pointer" onClick={() => navigate(`/update-product/${item._id}`)}>
                                          {getProductSupplierName(item) || '-'}
                                        </td>
                                        <td className="px-2 py-3 cursor-pointer" onClick={() => navigate(`/update-product/${item._id}`)}>
                                          <div className="flex items-center gap-1 text-xs">
                                            {item.price !== undefined && item.price !== null ? (
                                              <>
                                                <span className="text-gray-800">{Number(item.price).toFixed(2)}€</span>
                                                {item.purchasePrice !== undefined && item.purchasePrice !== null && Number(item.price) !== 0 && (
                                                  <span className={`font-medium ${(Number(item.price) - Number(item.purchasePrice)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                    ({((Number(item.price) - Number(item.purchasePrice)) / Number(item.price) * 100).toFixed(0)}%)
                                                  </span>
                                                )}
                                              </>
                                            ) : (
                                              <span className="text-gray-400">-</span>
                                            )}
                                          </div>
                                        </td>
                                        <td className="px-2 py-3 cursor-pointer" onClick={() => navigate(`/update-product/${item._id}`)}>
                                        {(() => {
                                            const tag = resolveProductTag(item);
                                            return tag ? (
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="inline-block w-4 h-4 rounded-full border border-white shadow-sm"
                                                style={{ backgroundColor: tag.color }}
                                                title={tag.name}
                                            />
                                            <span className="text-xs text-gray-700">{tag.name}</span>
                                        </div>
                                            ) : (
                                            '-'
                                            );
                                        })()}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              {filteredStock.length === 0 && (
                                <div className="p-6 text-center text-gray-500">Aucun produit</div>
                              )}
                            </div>
                          )}
                    
                            {/* View cards */}
                          {view === 'grid' && (
                            <div className="z-0 col-span-12 mb-10 mt-2 grid h-full w-full grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                              {filteredStock.map((item: any, index: number) => (
                                <StockCard
                                  id={item._id}
                                  key={index}
                                  name={item.name}
                                  type={item.type}
                                  alert={item.alert}
                                  imgs={item.imageUrls}
                                  img={item.imageUrl}
                                  productType={item.productType}
                                  unity={item.unity}
                                  quantity={item.quantity}
                                  reference={item.ref}
                                  color={item.colorCode}
                                />

                              ))}
                              {filteredStock.length === 0 && (
                                <div className="col-span-12 p-6 text-center text-gray-500">Aucun produit</div>
                              )}
                            </div>
                          )}
                        </>
                      );
                    })()}
                    
                    {/* nothing product */}
                    {stock &&
                      toggleState < 5 &&
                      filterStockPerZone(stock, toggleState).length === 0 &&
                      stock.length !== 0 && (
                        <div className="col-span-12">
                          <img
                            className="mx-auto flex h-[120px] w-[120px] items-center justify-center"
                            src={mobilityImg}
                            alt=""
                          />
                          <p className="mt-3 text-center">
                            Déplacez du stock de zone en zone en faisant une entrée-sortie.
                          </p>
                          <p className="mt-3 text-center">Vous pourrez bientôt le faire ici en 2 cliques !</p>
                          <div className="font-large flex items-center justify-center rounded-xl bg-white py-2 text-base text-gray-600 dark:!bg-navy-900 dark:text-white">
                            <button
                              className="linear mt-5 flex items-center justify-center rounded-xl bg-toola-500 px-10 py-2 text-base font-medium text-white hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                              onClick={() => setToggleState(5)}
                            >
                              Mouvement de stock
                            </button>
                          </div>
                        </div>
                    )}
                    
                    {stock && stock.length === 0 && toggleState !== 5 && (
                      <div className="col-span-12">
                        <img
                          className="mx-auto flex h-[120px] w-[120px] items-center justify-center"
                          src={ingredientsImg}
                          alt=""
                        />
                        <div className="font-large flex items-center justify-center rounded-xl bg-white py-2 text-base text-gray-600 dark:!bg-navy-900 dark:text-white">
                          <button
                            className="linear mt-5 flex items-center justify-center rounded-xl bg-[#0260CB] px-10 py-2 text-base font-medium text-white hover:bg-[#01499b] active:bg-[#024086] dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
                            onClick={() => navigate('/create-product')}
                          >
                            Créer votre premier produit
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {toggleState === 5 && <StockMovement />}
                </div>
            </Card>
        </>
    );
};

export default StockList;
